import { Editor } from "@monaco-editor/react";
import {
  Close as CloseIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  OpenInNew as OpenInNewIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Chip, Drawer, IconButton, Link, Paper, Stack, Tab, Typography } from "@mui/material";
import type React from "react";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router";
import { STATUS_COLORS } from "@/theme/theme";
import { useApiQuery } from "../../hooks/use-api-query.ts";
import type { Check, CheckRun, CheckRunLogEntry, CheckSchedule } from "../../types.ts";
import { RunStatusChip } from "../run-status-chip.tsx";

// Helper to get background color for log entry
function getLogBackgroundColor(status: CheckRunLogEntry["status"]): string {
  switch (status) {
    case "succeeded":
    case "dependency_completed":
      return STATUS_COLORS.success.bg;
    case "failed":
    case "dependency_failed":
      return STATUS_COLORS.error.bg;
    case "retrying":
      return STATUS_COLORS.warning.bg;
    case "dependency_started":
      return STATUS_COLORS.info.bg;
    case "dependency_cache_hit":
      return STATUS_COLORS.purple.bg;
    default:
      return STATUS_COLORS.default.bg;
  }
}

// Helper to get chip color for log status
function getLogStatusChipColor(status: CheckRunLogEntry["status"]): string {
  switch (status) {
    case "succeeded":
    case "dependency_completed":
      return STATUS_COLORS.success.text;
    case "failed":
    case "dependency_failed":
      return STATUS_COLORS.error.text;
    case "retrying":
      return STATUS_COLORS.warning.text;
    case "dependency_started":
      return STATUS_COLORS.info.text;
    case "dependency_cache_hit":
      return STATUS_COLORS.purple.text;
    default:
      return STATUS_COLORS.default.text;
  }
}

// Helper to get human-readable status label
function getLogStatusLabel(status: CheckRunLogEntry["status"]): string {
  switch (status) {
    case "dependency_started":
      return "dependency";
    case "dependency_completed":
      return "completed";
    case "dependency_failed":
      return "failed";
    case "dependency_cache_hit":
      return "cache hit";
    default:
      return status;
  }
}

// Check if this is a dependency log entry
function isDependencyLog(log: CheckRunLogEntry): boolean {
  return log.status.startsWith("dependency_");
}

interface CheckRunDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  checkRun: CheckRun;
  checkId: string;
}

// Full CheckRun with request/response data
interface FullCheckRun extends CheckRun {
  request?: {
    url: string;
    method: string;
    headers: Record<string, string>;
    body?: string;
  };
  response?: {
    status_code: number;
    headers: Record<string, string>;
    body: string;
    time_ms: number;
  };
}

// Schedule run with its check runs (subset of fields used here).
interface ScheduleRunWithChecks {
  id: string;
  check_runs?: CheckRun[];
}

// Standalone check-chain run (DES-467): every run sharing a root_check_run_id
// plus the computed dependency graph.
interface CheckChainRun {
  check_id: string;
  check_name?: string;
  run_status: CheckRun["run_status"];
}
interface CheckChainResponse {
  root_check_run_id: string;
  graph: Record<string, string[]>;
  runs: CheckChainRun[];
}

interface ChecksListResponse {
  checks: Check[];
  total: number;
}

export default function CheckRunDetailsDrawer({
  open,
  onClose,
  checkRun,
  checkId,
}: CheckRunDetailsDrawerProps) {
  const [tab, setTab] = useState<"overview" | "request" | "response" | "variables">("overview");
  const [revealedVars, setRevealedVars] = useState<Set<string>>(new Set());

  // Fetch full run details if we need request/response
  const { data: fullRun } = useApiQuery<FullCheckRun>(`/checks/${checkId}/runs/${checkRun.id}`, {
    enabled: open && !!checkRun.id,
  });

  const run = fullRun || checkRun;

  // Schedule context: when this run is part of a schedule run, surface the
  // upstream dependency execution path.
  const scheduleId = run.check_schedule_id;
  const scheduleRunId = run.check_schedule_run_id;
  const hasScheduleContext = !!scheduleId && !!scheduleRunId;

  // Schedule (for the dependency graph).
  const { data: schedule } = useApiQuery<CheckSchedule>(`/check-schedules/${scheduleId ?? ""}`, {
    enabled: open && hasScheduleContext,
  });

  // Schedule run (for sibling check run statuses).
  const { data: scheduleRun } = useApiQuery<ScheduleRunWithChecks>(
    `/check-schedules/${scheduleId ?? ""}/runs/${scheduleRunId ?? ""}`,
    {
      enabled: open && hasScheduleContext,
    },
  );

  // Checks in the schedule's app config (to resolve names).
  const { data: checksResponse } = useApiQuery<ChecksListResponse>(
    schedule?.app_config_id ? `/checks?app_config_id=${schedule.app_config_id}` : "/checks",
    {
      enabled: open && hasScheduleContext && !!schedule?.app_config_id,
    },
  );

  // Standalone check-chain context (DES-467): when this run executed its
  // dependency chain (and is not part of a schedule run), surface the same
  // dependency-path UI from the chain endpoint.
  const rootCheckRunId = run.root_check_run_id;
  const hasChainContext = !!rootCheckRunId && !hasScheduleContext;

  const { data: chain } = useApiQuery<CheckChainResponse>(
    `/checks/${checkId}/runs/${checkRun.id}/chain`,
    {
      enabled: open && hasChainContext,
    },
  );

  // Upstream dependency path: the checks this check depended on, with their
  // run status. Derived by inverting the dependency graph
  // ({ key: [dependents] }) to find this check's parents. Sourced from the
  // schedule (schedule runs) or the chain endpoint (standalone chain runs).
  const dependencyPath = useMemo(() => {
    const graph = hasChainContext ? chain?.graph : schedule?.check_dependency_graph;
    if (!graph) return [];
    const upstreamIds = new Set<string>();
    for (const [from, dependents] of Object.entries(graph)) {
      if (from === "root") continue;
      if (dependents.includes(checkId)) upstreamIds.add(from);
    }
    if (upstreamIds.size === 0) return [];

    const nameById = new Map<string, string>(
      hasChainContext
        ? (chain?.runs ?? []).flatMap((r) =>
            r.check_name ? [[r.check_id, r.check_name] as [string, string]] : [],
          )
        : (checksResponse?.checks ?? []).map((c) => [c.id, c.name]),
    );
    const statusById = new Map(
      hasChainContext
        ? (chain?.runs ?? []).map((r) => [r.check_id, r.run_status])
        : (scheduleRun?.check_runs ?? []).map((cr) => [cr.check_id, cr.run_status]),
    );
    return [...upstreamIds].map((id) => ({
      id,
      name: nameById.get(id) || `Check ${id.slice(0, 8)}`,
      status: statusById.get(id),
    }));
  }, [hasChainContext, chain, schedule, checksResponse, scheduleRun, checkId]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setTab(newValue as "overview" | "request" | "response" | "variables");
  };

  const toggleRevealVar = (key: string) => {
    setRevealedVars((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const formatJson = (data: unknown): string => {
    try {
      if (typeof data === "string") {
        // Try to parse if it's a JSON string
        const parsed = JSON.parse(data);
        return JSON.stringify(parsed, null, 2);
      }
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 700, md: 900, lg: 1000 },
          p: 0,
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              Check Run Details
            </Typography>
            <RunStatusChip status={run.run_status} />
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Tabs */}
        <TabContext value={tab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList onChange={handleTabChange}>
              <Tab label="Overview" value="overview" />
              <Tab label="Request" value="request" disabled={!run.request} />
              <Tab label="Response" value="response" disabled={!run.response} />
              <Tab label="Variables" value="variables" disabled={!run.output_variables?.length} />
            </TabList>
          </Box>

          {/* Overview Tab */}
          <TabPanel value="overview" sx={{ flex: 1, overflow: "auto" }}>
            <Stack spacing={3}>
              {/* Status & Timing */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} mb={2}>
                  Execution Summary
                </Typography>
                <Stack direction="row" spacing={4} flexWrap="wrap">
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Box mt={0.5}>
                      <RunStatusChip status={run.run_status} />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Duration
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {run.duration_ms ? `${run.duration_ms}ms` : "-"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Retries
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {run.retry_count}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Trigger
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {run.trigger || "manual"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Created At
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {new Date(run.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* Validation Results */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} mb={2}>
                  Validation Results
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Validation Status
                    </Typography>
                    <Box mt={0.5}>
                      {run.validation_passed === null || run.validation_passed === undefined ? (
                        <Chip
                          label="Not Validated"
                          size="small"
                          sx={{
                            backgroundColor: STATUS_COLORS.default.bg,
                            color: STATUS_COLORS.default.text,
                          }}
                        />
                      ) : (
                        <Chip
                          label={run.validation_passed ? "Passed" : "Failed"}
                          size="small"
                          sx={{
                            backgroundColor: run.validation_passed
                              ? STATUS_COLORS.success.bg
                              : STATUS_COLORS.error.bg,
                            color: run.validation_passed
                              ? STATUS_COLORS.success.text
                              : STATUS_COLORS.error.text,
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  {run.validation_reasoning && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        AI Reasoning
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          mt: 0.5,
                          backgroundColor: "background.default",
                        }}
                      >
                        <Typography variant="body2">{run.validation_reasoning}</Typography>
                      </Paper>
                    </Box>
                  )}
                </Stack>
              </Paper>

              {/* Error Message */}
              {run.error_message && (
                <Paper sx={{ p: 2, backgroundColor: STATUS_COLORS.error.bg }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1} color="error">
                    Error
                  </Typography>
                  <Typography variant="body2" color="error.dark">
                    {run.error_message}
                  </Typography>
                </Paper>
              )}

              {/* Dependency Execution Path (schedule runs + chain runs) */}
              {(hasScheduleContext || hasChainContext) && (
                <Paper sx={{ p: 2 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    mb={dependencyPath.length > 0 ? 2 : 0}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      Dependency Path
                    </Typography>
                    {hasChainContext && (
                      <Chip
                        label="chain run"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.7rem",
                          backgroundColor: STATUS_COLORS.purple.bg,
                          color: STATUS_COLORS.purple.text,
                        }}
                      />
                    )}
                    {run.check_schedule_name && (
                      <Chip
                        label={run.check_schedule_name}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.7rem",
                          backgroundColor: STATUS_COLORS.info.bg,
                          color: STATUS_COLORS.info.text,
                        }}
                      />
                    )}
                  </Stack>
                  {dependencyPath.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No dependencies — this check ran without upstream checks.
                    </Typography>
                  ) : (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {dependencyPath.map((dep, idx) => (
                        <Stack key={dep.id} direction="row" spacing={1} alignItems="center">
                          {idx > 0 && (
                            <KeyboardArrowRightIcon sx={{ color: "grey.400", fontSize: 18 }} />
                          )}
                          <Link
                            component={RouterLink}
                            to={`/checks/${dep.id}`}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              textDecoration: "none",
                            }}
                          >
                            <Chip
                              label={dep.name}
                              size="small"
                              clickable
                              sx={{ height: 24, fontSize: "0.75rem" }}
                            />
                          </Link>
                          {dep.status && <RunStatusChip status={dep.status} />}
                        </Stack>
                      ))}
                      <KeyboardArrowRightIcon sx={{ color: "grey.400", fontSize: 18 }} />
                      <Chip
                        label="This check"
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          backgroundColor: STATUS_COLORS.default.bg,
                        }}
                      />
                    </Stack>
                  )}
                </Paper>
              )}

              {/* Execution Log */}
              {run.logs && run.logs.length > 0 && (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>
                    Execution Log
                  </Typography>
                  <Stack spacing={1}>
                    {run.logs.map((log: CheckRunLogEntry, index: number) => (
                      <Box
                        key={index}
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          backgroundColor: getLogBackgroundColor(log.status),
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                          {/* Show "Dependency" or "Attempt X" based on log type */}
                          {isDependencyLog(log) ? (
                            <Chip
                              label="Dependency"
                              size="small"
                              sx={{
                                minWidth: 80,
                                backgroundColor: STATUS_COLORS.info.bg,
                                color: STATUS_COLORS.info.text,
                              }}
                            />
                          ) : (
                            <Chip
                              label={`Attempt ${log.attempt}`}
                              size="small"
                              sx={{ minWidth: 80 }}
                            />
                          )}
                          <Chip
                            label={getLogStatusLabel(log.status)}
                            size="small"
                            sx={{
                              backgroundColor: getLogStatusChipColor(log.status),
                              color: "common.white",
                            }}
                          />
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {log.message}
                            {/* Show cache expiry for cache hits */}
                            {log.cache_expires_in_seconds !== undefined &&
                              log.cache_expires_in_seconds !== null && (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ ml: 1 }}
                                >
                                  (expires in {log.cache_expires_in_seconds}s)
                                </Typography>
                              )}
                          </Typography>
                          {/* View Run link for dependency logs with test_run_id */}
                          {isDependencyLog(log) && log.test_run_id && (
                            <Link
                              component={RouterLink}
                              to={`/tests/${log.test_id}?tab=runs&runId=${log.test_run_id}`}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                fontSize: "0.75rem",
                                textDecoration: "none",
                                color: "primary.main",
                                "&:hover": {
                                  textDecoration: "underline",
                                },
                              }}
                            >
                              View Run
                              <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </Link>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </Typography>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              )}
            </Stack>
          </TabPanel>

          {/* Request Tab */}
          <TabPanel value="request" sx={{ flex: 1, overflow: "auto", p: 0 }}>
            {run.request ? (
              <Stack spacing={0} sx={{ height: "100%" }}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Chip
                      label={run.request.method}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        backgroundColor: STATUS_COLORS.info.bg,
                        color: STATUS_COLORS.info.text,
                      }}
                    />
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                      {run.request.url}
                    </Typography>
                  </Stack>
                </Box>

                {run.request.headers && Object.keys(run.request.headers).length > 0 && (
                  <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                    <Typography variant="subtitle2" fontWeight={600} mb={1}>
                      Headers
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        backgroundColor: STATUS_COLORS.default.bg,
                        p: 1,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                        overflow: "auto",
                        margin: 0,
                      }}
                    >
                      {formatJson(run.request.headers)}
                    </Box>
                  </Box>
                )}

                {run.request.body && (
                  <Box sx={{ flex: 1, minHeight: 200 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ p: 2, pb: 1 }}>
                      Body
                    </Typography>
                    <Editor
                      height="300px"
                      defaultLanguage="json"
                      value={formatJson(run.request.body)}
                      options={{
                        readOnly: true,
                        minimap: { enabled: false },
                        lineNumbers: "off",
                        folding: true,
                        scrollBeyondLastLine: false,
                        wordWrap: "on",
                      }}
                    />
                  </Box>
                )}
              </Stack>
            ) : (
              <Box sx={{ p: 3 }}>
                <Typography color="text.secondary">No request data available</Typography>
              </Box>
            )}
          </TabPanel>

          {/* Response Tab */}
          <TabPanel value="response" sx={{ flex: 1, overflow: "auto", p: 0 }}>
            {run.response ? (
              <Stack spacing={0} sx={{ height: "100%" }}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Chip
                      label={`${run.response.status_code}`}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        backgroundColor:
                          run.response.status_code >= 200 && run.response.status_code < 300
                            ? STATUS_COLORS.success.bg
                            : run.response.status_code >= 400
                              ? STATUS_COLORS.error.bg
                              : STATUS_COLORS.warning.bg,
                        color:
                          run.response.status_code >= 200 && run.response.status_code < 300
                            ? STATUS_COLORS.success.text
                            : run.response.status_code >= 400
                              ? STATUS_COLORS.error.text
                              : STATUS_COLORS.warning.text,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {run.response.time_ms}ms
                    </Typography>
                  </Stack>
                </Box>

                {run.response.headers && Object.keys(run.response.headers).length > 0 && (
                  <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                    <Typography variant="subtitle2" fontWeight={600} mb={1}>
                      Headers
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        backgroundColor: STATUS_COLORS.default.bg,
                        p: 1,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                        overflow: "auto",
                        margin: 0,
                        maxHeight: 150,
                      }}
                    >
                      {formatJson(run.response.headers)}
                    </Box>
                  </Box>
                )}

                <Box sx={{ flex: 1, minHeight: 200 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ p: 2, pb: 1 }}>
                    Body
                  </Typography>
                  <Editor
                    height="400px"
                    defaultLanguage="json"
                    value={formatJson(run.response.body)}
                    options={{
                      readOnly: true,
                      minimap: { enabled: false },
                      lineNumbers: "off",
                      folding: true,
                      scrollBeyondLastLine: false,
                      wordWrap: "on",
                    }}
                  />
                </Box>
              </Stack>
            ) : (
              <Box sx={{ p: 3 }}>
                <Typography color="text.secondary">No response data available</Typography>
              </Box>
            )}
          </TabPanel>

          {/* Variables Tab — output variables captured by this check run */}
          <TabPanel value="variables" sx={{ flex: 1, overflow: "auto" }}>
            {run.output_variables && run.output_variables.length > 0 ? (
              <Stack spacing={1.5}>
                {run.output_variables.map((variable) => {
                  const isRevealed = revealedVars.has(variable.key);
                  const rawValue = variable.value.toString();
                  const displayValue =
                    variable.is_sensitive && !isRevealed
                      ? "•".repeat(Math.min(rawValue.length, 24)) || "••••••"
                      : rawValue;
                  return (
                    <Paper key={variable.key} variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={1}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                          useFlexGap
                        >
                          <Typography variant="subtitle2" fontWeight={600}>
                            {variable.key}
                          </Typography>
                          <Chip
                            label={variable.type}
                            size="small"
                            sx={{ height: 20, fontSize: "0.7rem" }}
                          />
                          <Chip
                            label={variable.context}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: "0.7rem" }}
                          />
                          {variable.is_sensitive && (
                            <Chip
                              label="sensitive"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.7rem",
                                backgroundColor: STATUS_COLORS.warning.bg,
                                color: STATUS_COLORS.warning.text,
                              }}
                            />
                          )}
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box
                            component="pre"
                            sx={{
                              flex: 1,
                              backgroundColor: STATUS_COLORS.default.bg,
                              p: 1,
                              borderRadius: 1,
                              fontSize: "0.75rem",
                              overflow: "auto",
                              margin: 0,
                              wordBreak: "break-all",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {displayValue}
                          </Box>
                          {variable.is_sensitive && (
                            <IconButton
                              size="small"
                              onClick={() => toggleRevealVar(variable.key)}
                              aria-label={isRevealed ? "Hide value" : "Reveal value"}
                            >
                              {isRevealed ? (
                                <VisibilityOffIcon fontSize="small" />
                              ) : (
                                <VisibilityIcon fontSize="small" />
                              )}
                            </IconButton>
                          )}
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            ) : (
              <Box sx={{ p: 3 }}>
                <Typography color="text.secondary">No output variables captured</Typography>
              </Box>
            )}
          </TabPanel>
        </TabContext>
      </Box>
    </Drawer>
  );
}
